import React from "react";

/**
 * IgitaboLogo — Shield + Community Network mark
 * Inline SVG · no PNG · crisp at any size · works on any background
 *
 * Props:
 *   size      — icon height/width in px (default 40)
 *   variant   — "icon" | "full" (default "full")
 *   className — wrapper className
 *   style     — wrapper style override
 */
const IgitaboLogo = ({ size = 40, variant = "full", className = "", style = {} }) => {

  // Each render gets unique gradient IDs to avoid SVG defs collision
  // when multiple instances are on the same page.
  const uid = React.useId().replace(/:/g, "");

  const Icon = ({ s }) => (
    <svg
      width={s}
      height={s}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Inkingi logo"
      style={{
        display: "block",
        flexShrink: 0,
        filter: "drop-shadow(0 2px 10px rgba(0,0,0,0.45))",
      }}
    >
      <defs>
        {/* Background */}
        <linearGradient id={`${uid}-bg`} x1="15%" y1="0%" x2="85%" y2="100%">
          <stop offset="0%"   stopColor="#162d5e"/>
          <stop offset="100%" stopColor="#091428"/>
        </linearGradient>

        {/* Shield interior */}
        <linearGradient id={`${uid}-sf`} x1="20%" y1="0%" x2="80%" y2="100%">
          <stop offset="0%"   stopColor="#1e4080"/>
          <stop offset="100%" stopColor="#0f2550"/>
        </linearGradient>

        {/* Node fill */}
        <linearGradient id={`${uid}-nf`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor="#ffffff"/>
          <stop offset="100%" stopColor="#d0e4ff"/>
        </linearGradient>

        {/* Green accent */}
        <linearGradient id={`${uid}-gr`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor="#2ecc7a"/>
          <stop offset="100%" stopColor="#18a058"/>
        </linearGradient>

        <clipPath id={`${uid}-clip`}>
          <rect width="64" height="64" rx="14" ry="14"/>
        </clipPath>
      </defs>

      {/* ── Container ── */}
      <rect width="64" height="64" rx="14" ry="14" fill={`url(#${uid}-bg)`}/>
      <ellipse cx="18" cy="14" rx="20" ry="12"
               fill="rgba(255,255,255,0.06)" clipPath={`url(#${uid}-clip)`}/>

      {/* ── Shield ── */}
      <path
        d="M32 8 L14 8 C10 8 8 10 8 14 L8 36 C8 46 18 54 32 58 C46 54 56 46 56 36 L56 14 C56 10 54 8 50 8 Z"
        fill={`url(#${uid}-sf)`}
      />
      {/* Shield border */}
      <path
        d="M32 9.5 L15 9.5 C11.5 9.5 9.5 11.5 9.5 15 L9.5 36 C9.5 45 19 52.5 32 56.5 C45 52.5 54.5 45 54.5 36 L54.5 15 C54.5 11.5 52.5 9.5 49 9.5 Z"
        fill="none" stroke="#4a90e8" strokeWidth="1.5" opacity="0.85"
      />
      {/* Top-edge inner highlight */}
      <path d="M16 12 L32 12" stroke="rgba(255,255,255,0.14)" strokeWidth="1.5" strokeLinecap="round"/>

      {/* ── Connection lines ── */}
      <line x1="32" y1="24" x2="20" y2="40" stroke="#4a90e8" strokeWidth="1.8" strokeLinecap="round" opacity="0.65"/>
      <line x1="32" y1="24" x2="44" y2="40" stroke="#4a90e8" strokeWidth="1.8" strokeLinecap="round" opacity="0.65"/>
      <line x1="20" y1="40" x2="44" y2="40" stroke="#4a90e8" strokeWidth="1.8" strokeLinecap="round" opacity="0.65"/>

      {/* ── Citizen nodes (bottom) ── */}
      <circle cx="20" cy="40" r="4"   fill={`url(#${uid}-nf)`}/>
      <circle cx="20" cy="40" r="1.8" fill="#2563c8"/>
      <circle cx="44" cy="40" r="4"   fill={`url(#${uid}-nf)`}/>
      <circle cx="44" cy="40" r="1.8" fill="#2563c8"/>

      {/* ── Leader node (top) — green ── */}
      <circle cx="32" cy="24" r="5.5" fill={`url(#${uid}-gr)`}/>
      <circle cx="32" cy="24" r="2.2" fill="rgba(255,255,255,0.95)"/>

      {/* ── Chevron — progress / leadership ── */}
      <path
        d="M24 20 L32 14 L40 20"
        fill="none" stroke="white" strokeWidth="2.2"
        strokeLinecap="round" strokeLinejoin="round" opacity="0.88"
      />

      {/* ── Container border ── */}
      <rect x="0.75" y="0.75" width="62.5" height="62.5" rx="13.5" ry="13.5"
            fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1.5"/>
    </svg>
  );

  if (variant === "icon") {
    return <Icon s={size} />;
  }

  return (
    <div
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: Math.round(size * 0.28),
        userSelect: "none",
        ...style,
      }}
    >
      <Icon s={size} />

      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
        {/* Primary wordmark */}
        <span
          style={{
            fontSize: Math.round(size * 0.40),
            fontWeight: 700,
            letterSpacing: "-0.02em",
            color: "#ffffff",
            lineHeight: 1.1,
          }}
        >
          Inkingi
        </span>
        {/* Tagline */}
        <span
          style={{
            fontSize: Math.round(size * 0.175),
            fontWeight: 500,
            letterSpacing: "0.13em",
            textTransform: "uppercase",
            color: "rgba(160,200,255,0.55)",
            marginTop: Math.round(size * 0.06),
            lineHeight: 1,
          }}
        >
          y'iterambere
        </span>
      </div>
    </div>
  );
};

export default IgitaboLogo;
